import { cssObj } from '@/css/resources/functions.css';
import { fluidValues } from '@/css/resources/mixins.css';
import { breakpoints, transitions } from '@/css/resources/variables.css';
import { keyframes } from '@emotion/css';

const SLIDE_DURATION = '0.9s';
const SLIDE_EASING = 'cubic-bezier(0.65, 0, 0.35, 1)';

const slideInFromRight = keyframes`
	from {
		transform: translateX(100%);
	}
	to {
		transform: translateX(0);
	}
`;
const slideInFromLeft = keyframes`
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
`;

const getCustomStyles = () =>
	cssObj({
		'.supt-section--header-a': cssObj(
			fluidValues('paddingBottom', 66, 33),
			{
				paddingTop: 0,

				[`@media (max-width: calc(${breakpoints.medium} - 1px))`]: {
					paddingTop: '80px',
				},

				'.supt-section__inner': {
					position: 'relative',

					'.supt-section__headline': {
						[`@media (min-width: ${breakpoints.medium})`]: {
							gridRow: '2/3',
							paddingTop: 40,
						},

						'.supt-section__uptitle': {
							margin: '0 0 14px 0',
						},
						'.supt-section__title': {
							margin: 0,
							paddingTop: '0.15em',
						},
						'.supt-section__introduction': cssObj(
							fluidValues('marginTop', 20, 30),
							{
								marginBottom: 0,
							}
						),
					},
					'.supt-section__col--left': {
						[`@media (min-width: ${breakpoints.medium})`]: {
							gridRow: '3/4',
						},
					},
					'.supt-section__col--right': {
						[`@media (max-width: calc(${breakpoints.medium} - 1px))`]:
							{
								marginTop: '40px',
							},

						[`@media (min-width: ${breakpoints.medium})`]: {
							gridRow: '1/5',
						},

						'.supt-figure': {
							display: 'flex',
							flexDirection: 'column',

							span: {
								width: '100% !important',
							},

							figcaption: {
								textAlign: 'right',
							},
						},

						// when several images are set, they are displayed in a slider
						'&.supt-section__col--hasSlider': {
							// keeps the slider above the "whole section" click overlay
							// so dragging & navigating stay possible
							position: 'relative',
							zIndex: 2,

							'.supt-slider': {
								display: 'flex',
								flexDirection: 'column-reverse',
							},

							// slides are stacked on top of each other: the active one stays
							// still while the incoming one slides over it
							'.supt-slider__container': {
								display: 'grid',
								overflow: 'hidden',
								paddingTop: 0,
								touchAction: 'pan-y',
							},

							'.supt-slider__slide': {
								gridArea: '1 / 1',
								zIndex: 0,
								visibility: 'hidden',

								'&.is-previous': {
									zIndex: 1,
									visibility: 'visible',
								},
								'&.is-active': {
									zIndex: 2,
									visibility: 'visible',
								},
								'&.is-entering': {
									animation: `${slideInFromRight} ${SLIDE_DURATION} ${SLIDE_EASING} both`,

									'&.from-left': {
										animationName: slideInFromLeft,
									},
								},

								'.supt-figure': {
									display: 'block',
									height: '100%',
									'> *': {
										height: '100% !important',
									},
								},

								img: {
									objectFit: 'cover',
									userSelect: 'none',
									WebkitUserDrag: 'none',
								},
							},

							// navigation: a pill holding the prev, play/pause & next buttons
							'.supt-slider__controls': {
								display: 'flex',
								justifyContent: 'center',
								transform: 'translateY(-50%)',
								zIndex: 5,
							},

							'.supt-slider__navigation': {
								position: 'static',
								width: 'auto',
								display: 'flex',
								alignItems: 'center',
								gap: 24,
								padding: '12px 16px',
								backgroundColor: '#fff',
								border: '1px solid #000',
								borderRadius: 100,

								'.supt-slider__button': {
									position: 'static',
									width: 'auto',
									height: 'auto',
									transform: 'none',
									backgroundColor: 'transparent',
									border: 0,
									borderRadius: 0,
									padding: 0,
									cursor: 'pointer',
									appearance: 'none',

									'&:hover, &:focus-visible': {
										backgroundColor: 'transparent',
									},

									// arrows: 18px icon box (clips the hover slide animation)
									span: {
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: 18,
										height: 18,

										svg: {
											width: 'auto',
											stroke: 'none',
										},
									},

									'&.supt-slider__button--prev svg': {
										transform: 'scaleX(-1)',
									},

									'&.supt-slider__button--toggle': {
										span: {
											width: 'auto',
											height: 14,
											overflow: 'visible',

											svg: {
												fill: '#000',
												transition: `opacity ${transitions.mid} ease`,
											},
										},
										'&:hover, &:focus-visible': {
											svg: {
												opacity: 0.5,
											},
										},
									},
								},
							},
						},
					},
				},

				'&:not(.supt-section--hasButtons)': {
					'.supt-section__inner': {
						'.supt-section__col--left': {
							display: 'none',
						},
					},
				},
				'&:not(.supt-section--hasImage)': cssObj(
					fluidValues('paddingTop', 110, 160),
					{
						'.supt-section__inner': {
							'.supt-section__col--right': {
								display: 'none',
							},
						},
					}
				),

				'.supt-button': {
					position: 'relative',
					zIndex: 2,
				},

				// make the first button of the first "buttons list" cover the whole section (doesn't apply to editor, thanks to the use of :not())
				':not(.wp-block) > .supt-listCards:first-of-type .supt-button:first-of-type':
					{
						position: 'initial',
						'&::after': {
							content: '""',
							display: 'block',
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							zIndex: 1,
						},
					},
			}
		),
	});

export { getCustomStyles };
